import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button, Form, Table } from "react-bootstrap";
import axios from "axios";
import Layout from "../../components/layouts/Main";
import config from "../../config";
import { toast } from "react-toastify";
import PaymentTermsSelect from "../../components/common/PaymentTermsSelect";

export default function AddQuotationPurchaseOrder({ onComplete }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);

    const [formData, setFormData] = useState({
        recipientType: "",
        vendorId: "",
        orderDate: new Date().toISOString().split("T")[0],
        expectedDeliveryDate: "",
        deliveryFrom: "",
        deliveryTo: "",
        tax: 0,
        notes: "",
        paymentTerm: "",
        items: []
    });

    const [quotationData, setQuotation] = useState(null);
    const [newItem, setNewItem] = useState({
        productId: "",
        quality: "",
        orderedQuantity: 0,
        unit: "",
        rate: 0,
        totalPrice: 0
    });

    const [vendorsList, setVendors] = useState([]);
    const [brokers, setBrokers] = useState([]);
    const [products, setProducts] = useState([]);

    // Fetch base data
    useEffect(() => {
        axios.get(`${config.API_BASE_URL}/vendors?approved=true&status=Active`).then(res => setVendors(res.data));
        axios.get(`${config.API_BASE_URL}/brokers?approved=true&status=Active`).then(res => setBrokers(res.data));
        axios.get(`${config.API_BASE_URL}/products?populateUnit=true`).then(res => setProducts(res.data));
        axios.get(`${config.API_BASE_URL}/rfq-responses/single/${id}`).then(res => setQuotation(res.data));
    }, [id]);

    // Populate form when quotation data is loaded
    useEffect(() => {
        if (quotationData && quotationData.rfq_recipient) {
            const { recipient_type, recipient_id } = quotationData.rfq_recipient;
            let vendorValue = "";
            let brokerValue = "";
            let deliveryFromAddr = "";

            if (recipient_type === "vendor") {
                vendorValue = recipient_id.vId;
                const v = vendorsList.find(v => v._id === recipient_id.vId);
                if (v) {
                    deliveryFromAddr = `${v.address}, ${v.city}, ${v.state}, ${v.country} - ${v.pin_code}`;
                }
            } else if (recipient_type === "broker") {
                brokerValue = recipient_id.bId;
            }

            // Set default values from quotation data
            const paymentTerms = quotationData.rfq?.payment_terms || '';
            const expectedDeliveryDate = quotationData.expected_delivery_date ?
                new Date(quotationData.expected_delivery_date).toISOString().split('T')[0] : '';

            // Get company address for delivery to
            let deliveryToAddr = '';
            axios.get(`${config.API_BASE_URL}/company-settings`).then(res => {
                const companyData = res.data;
                if (companyData?.address) {
                    const addr = companyData.address;
                    deliveryToAddr = `${addr.street}\n${addr.city} ${addr.state}\n${addr.pincode}`;
                    setFormData(prev => ({
                        ...prev,
                        deliveryTo: deliveryToAddr
                    }));
                }
            }).catch(err => {
                console.error('Error fetching company settings:', err);
            });

            setFormData(prev => ({
                ...prev,
                recipientType: recipient_type,
                quotationId: quotationData._id,
                vendorId: vendorValue,
                brokerId: brokerValue,
                paymentTerm: paymentTerms, // Set default payment terms
                expectedDeliveryDate: expectedDeliveryDate, // Set default delivery date
                deliveryTo: deliveryToAddr, // Company address will be set by the axios call above
                items: quotationData.items.map(item => ({
                    productId: item.product._id,
                    quality: item.quality,
                    orderedQuantity: item.quantity,
                    unit: item.quantity_unit,
                    rate: item.unit_rate,
                    totalPrice: item.total_price
                })),
                tax: quotationData.tax || 0,
                deliveryFrom: deliveryFromAddr
            }));
        }
    }, [quotationData, vendorsList]);

    const handleOrderChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleItemChange = (e) => {
        const { name, value } = e.target;
        let updatedItem = { ...newItem, [name]: value };

        if (name === "productId") {
            const prod = products.find(p => p._id === value);
            updatedItem.unit = prod?.unitId?.unitCode || "";
        }

        if (name === "orderedQuantity" || name === "rate") {
            const qty = name === "orderedQuantity" ? value : updatedItem.orderedQuantity;
            const rate = name === "rate" ? value : updatedItem.rate;
            updatedItem.totalPrice = parseFloat(qty || 0) * parseFloat(rate || 0);
        }

        setNewItem(updatedItem);
    };

    const addItem = () => {
        if (!newItem.productId || newItem.orderedQuantity <= 0 || newItem.rate <= 0) {
            toast.error("Please fill product, quantity, and unit price");
            return;
        }
        setFormData(prev => ({
            ...prev,
            items: [
                ...prev.items,
                {
                    productId: newItem.productId,
                    quality: newItem.quality,
                    orderedQuantity: newItem.orderedQuantity,
                    unit: newItem.unit,
                    rate: newItem.rate,
                    totalPrice: newItem.totalPrice
                }
            ]
        }));
        setNewItem({
            productId: "",
            quality: "",
            orderedQuantity: 0,
            unit: "",
            rate: 0,
            totalPrice: 0
        });
    };

    const removeItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        if (field === "orderedQuantity" || field === "rate") {
            newItems[index].totalPrice =
                parseFloat(newItems[index].orderedQuantity || 0) * parseFloat(newItems[index].rate || 0);
        }
        setFormData({ ...formData, items: newItems });
    };

    const handleSave = async () => {
        try {
            const res = await axios.post(`${config.API_BASE_URL}/purchase-orders/new`, formData);

            const orderId = res.data.order._id;

            await Promise.all(
                formData.items.map(it =>
                    axios.post(`${config.API_BASE_URL}/purchase-order-items/new`, {
                        ...it,
                        purchaseOrderId: orderId
                    })
                )
            );
            // toast.success("Purchase Order added successfully");
            navigate("/purchase-orders", { state: { toast: "Purchase Order added successfully" } });
        } catch (err) {
            console.error("Error saving purchase order", err);
            toast.error("Error saving purchase order");
        }
    };

    return (
        <Layout>
            <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item"><Link to="/approved-quotations">Approved Quotations</Link></li>
                    <li className="breadcrumb-item active">Add New Purchase Order</li>
                </ol>
            </nav>
            <div className="row mb-3">
                <h2 className="mb-3">Add Purchase Order # {quotationData?.quotationId || ""}</h2>

                {step === 1 && (
                    <Form>
                        <div className="row">
                            {formData.recipientType === "vendor" && (
                                <Form.Group className="col-md-4 mb-3">
                                    <Form.Label>Vendor</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={vendorsList.find(v => v._id === formData.vendorId)?.business_name || ""}
                                        readOnly
                                    />
                                </Form.Group>
                            )}

                            {formData.recipientType === "broker" && (
                                <>
                                    <Form.Group className="col-md-4 mb-3">
                                        <Form.Label>Broker</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={brokers.find(b => b._id === formData.brokerId)?.business_name || ""}
                                            readOnly
                                        />
                                    </Form.Group>

                                    <Form.Group className="col-md-4 mb-3">
                                        <Form.Label>Vendor</Form.Label>
                                        <Form.Select
                                            value={formData.vendor}
                                            onChange={(e) => {
                                                const vendorId = e.target.value;
                                                const v = vendorsList.find(v => v._id === vendorId);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    vendorId: vendorId,
                                                    deliveryFrom: v ? `${v.address}, ${v.city}, ${v.state}, ${v.country} - ${v.pin_code}` : ""
                                                }));
                                            }}
                                        >
                                            <option value="">Select Vendor</option>
                                            {vendorsList.map(v => (
                                                <option key={v._id} value={v._id}>{v.business_name}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </>
                            )}

                            <Form.Group className="col-md-4 mb-3">
                                <Form.Label>Order Date <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="date"
                                    name="orderDate"
                                    value={formData.orderDate}
                                    onChange={handleOrderChange}
                                />
                            </Form.Group>

                            <Form.Group className="col-md-4 mb-3">
                                <Form.Label>Expected Delivery Date</Form.Label>
                                <Form.Control
                                    type="date"
                                    name="expectedDeliveryDate"
                                    value={formData.expectedDeliveryDate}
                                    onChange={handleOrderChange}
                                />
                            </Form.Group>
                        </div>

                        <div className="row">
                            <Form.Group className="col-md-6 mb-3">
                                <PaymentTermsSelect
                                    value={formData.paymentTerm}
                                    onChange={(value) => setFormData(prev => ({ ...prev, paymentTerm: value }))}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="col-md-6 mb-3">
                                <Form.Label>Other Notes</Form.Label>
                                <Form.Control type="text" name="notes" value={formData.notes} onChange={handleOrderChange} />
                            </Form.Group>
                        </div>

                        <div className="row">
                            <Form.Group className="col-md-6 mb-3">
                                <Form.Label>Delivery To</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    name="deliveryTo"
                                    value={formData.deliveryTo}
                                    onChange={handleOrderChange}

                                />
                                <small className="text-muted">
                                    Auto-filled with company address. Cannot be changed.
                                </small>
                            </Form.Group>

                            <Form.Group className="col-md-6 mb-3">
                                <Form.Label>Delivery From</Form.Label>
                                <Form.Control as="textarea" rows={4} name="deliveryFrom" value={formData.deliveryFrom} onChange={handleOrderChange} />
                            </Form.Group>
                        </div>
                        <Button onClick={() => setStep(2)} className="mt-2">Next</Button>
                    </Form>
                )}

                {step === 2 && (
                    <>
                        {/* Add Item Form */}
                        <Form className="mb-3">
                            <div className="row g-2 align-items-end">
                                <div className="col-md-3 form-floating">
                                    <Form.Select
                                        id="productSelect"
                                        name="productId"
                                        value={newItem.productId}
                                        onChange={handleItemChange}
                                    >
                                        <option value="">Select Product</option>
                                        {products.map(p => (
                                            <option key={p._id} value={p._id}>{p.productName}</option>
                                        ))}
                                    </Form.Select>
                                    <label htmlFor="productSelect">Product</label>
                                </div>

                                <div className="col-md-2 form-floating">
                                    <Form.Control
                                        id="qualityInput"
                                        name="quality"
                                        placeholder="Quality"
                                        value={newItem.quality}
                                        onChange={handleItemChange}
                                    />
                                    <label htmlFor="qualityInput">Quality</label>
                                </div>

                                <div className="col-md-2 position-relative">
                                    <div className="form-floating">
                                        <Form.Control
                                            id="quantityInput"
                                            type="number"
                                            name="orderedQuantity"
                                            placeholder="Quantity"
                                            value={newItem.orderedQuantity}
                                            onChange={handleItemChange}
                                        />
                                        <label htmlFor="quantityInput">Quantity</label>
                                    </div>
                                    {newItem.unit && (
                                        <span style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", fontWeight: "bold", color: "#555" }}>
                                            {newItem.unit}
                                        </span>
                                    )}
                                </div>

                                <div className="col-md-2 form-floating">
                                    <Form.Control
                                        id="rateInput"
                                        type="number"
                                        name="rate"
                                        placeholder="Unit Price"
                                        value={newItem.rate}
                                        onChange={handleItemChange}
                                    />
                                    <label htmlFor="rateInput">Unit Price (₹)</label>
                                </div>

                                <div className="col-md-2 form-floating">
                                    <Form.Control
                                        id="totalPriceInput"
                                        type="number"
                                        value={(newItem.orderedQuantity * newItem.rate).toFixed(2)}
                                        readOnly
                                    />
                                    <label htmlFor="totalPriceInput">Total (₹)</label>
                                </div>

                                <div className="col-md-1">
                                    <Button variant="success" onClick={addItem} className="w-100">+</Button>
                                </div>
                            </div>
                        </Form>

                        {/* Items Table */}
                        <Table>
                            <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Quality</th>
                                    <th>Quantity</th>
                                    <th>Unit Rate</th>
                                    <th>Total Price</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {formData.items.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <Form.Select
                                                value={item.productId}
                                                onChange={(e) => updateItem(index, 'productId', e.target.value)}
                                            >
                                                {products.map(p => (
                                                    <option key={p._id} value={p._id}>{p.productName}</option>
                                                ))}
                                            </Form.Select>
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={item.quality}
                                                onChange={(e) => updateItem(index, 'quality', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                value={item.orderedQuantity}
                                                onChange={(e) => updateItem(index, 'orderedQuantity', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="number"
                                                value={item.rate}
                                                onChange={(e) => updateItem(index, 'rate', e.target.value)}
                                            />
                                        </td>
                                        <td>{(item.orderedQuantity * item.rate).toFixed(2)}</td>
                                        <td><Button variant="danger" size="sm" onClick={() => removeItem(index)}>X</Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {/* Tax & Grand Total */}
                        {formData.items.length > 0 && (
                            <div className="row mt-3">
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>Tax (%)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="tax"
                                            value={formData.tax}
                                            onChange={(e) => setFormData(prev => ({ ...prev, tax: e.target.value }))}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label>Grand Total (₹)</Form.Label>
                                        <Form.Control
                                            value={(() => {
                                                const subtotal = formData.items.reduce((sum, it) => sum + (it.orderedQuantity * it.rate), 0);
                                                const taxAmount = subtotal * ((parseFloat(formData.tax) || 0) / 100);
                                                return (subtotal + taxAmount).toFixed(2);
                                            })()}
                                            readOnly
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}

                        <div className="d-flex justify-content-between mt-4">
                            <Button onClick={() => setStep(1)}>Back</Button>
                            <Button variant="success" onClick={handleSave}>
                                Save Purchase Order
                            </Button>
                        </div>
                    </>
                )}
            </div>

        </Layout >
    );
}
